import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * @typedef {HTMLElement & {
 *   pauseMedia?: () => void,
 *   loadContent?: (focus?: boolean) => void,
 *   playMedia?: () => void
 * }} DeferredMediaElement
 */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    if (!this.#isDesktopViewport()) {
      this.#queueSelectedSlideAutoplaySync();
    }
    if (this.dataset.syncAutoplayOnConnect === 'true' && this.#isDesktopViewport()) {
      delete this.dataset.syncAutoplayOnConnect;
      this.presentation === 'grid' ? this.#queueGridAutoplaySync() : this.#queueActiveSlideAutoplaySync();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (event.detail.data.morphedMain) {
      if (!this.#isDesktopViewport()) {
        this.#queueSelectedSlideAutoplaySync();
        return;
      }
      if (this.presentation === 'grid') {
        this.#queueGridAutoplaySync();
        return;
      }
      this.#queueActiveSlideAutoplaySync();
      return;
    }

    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;
    if (this.#isDesktopViewport()) {
      newMediaGallery.dataset.syncAutoplayOnConnect = 'true';
    }
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slideshow selection changes by autoplaying active video slides.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (this.presentation === 'grid' && this.#isDesktopViewport()) return;
    const selectedSlide = event.detail.slide;
    if (!(selectedSlide instanceof HTMLElement)) return;
    this.#syncAutoplayForSlide(selectedSlide);
  };

  #syncAutoplayForSlide(selectedSlide) {
    const autoplayMedia = /** @type {DeferredMediaElement[]} */ ([...this.querySelectorAll('deferred-media[autoplay]')]);
    if (!autoplayMedia.length) return;

    const selectedAutoplayMedia = /** @type {DeferredMediaElement | null} */ (
      selectedSlide.querySelector('deferred-media[autoplay]')
    );

    if (!(selectedAutoplayMedia instanceof HTMLElement)) {
      return;
    }

    for (const media of autoplayMedia) {
      if (media === selectedAutoplayMedia) continue;
      if (typeof media.pauseMedia === 'function') {
        media.pauseMedia();
      }
    }

    const hasInlineLoadedMedia = selectedAutoplayMedia.querySelector('video, iframe[data-video-type]') != null;

    if (
      typeof selectedAutoplayMedia.loadContent === 'function' &&
      !selectedAutoplayMedia.hasAttribute('data-media-loaded') &&
      !hasInlineLoadedMedia
    ) {
      selectedAutoplayMedia.loadContent(false);
    }

    this.#ensureSelectedAutoplayPlayback(selectedAutoplayMedia, selectedSlide);
  }

  #queueActiveSlideAutoplaySync() {
    requestAnimationFrame(() => {
      this.#syncActiveSlideAutoplay();
    });

    window.setTimeout(() => {
      this.#syncActiveSlideAutoplay();
    }, 180);
  }

  #queueSelectedSlideAutoplaySync() {
    requestAnimationFrame(() => {
      this.#syncSelectedSlideAutoplay();
    });

    window.setTimeout(() => {
      this.#syncSelectedSlideAutoplay();
    }, 180);

    window.setTimeout(() => {
      this.#syncSelectedSlideAutoplay();
    }, 420);
  }

  #syncSelectedSlideAutoplay() {
    if (!this.isConnected) return;

    const activeSlide = this.#getActiveSlide();
    if (!(activeSlide instanceof HTMLElement)) return;
    if (!activeSlide.querySelector('deferred-media[autoplay]')) return;

    this.#syncAutoplayForSlide(activeSlide);
  }

  #syncActiveSlideAutoplay() {
    if (!this.isConnected) return;

    const activeSlide = this.#getActiveSlide();
    if (activeSlide instanceof HTMLElement && activeSlide.querySelector('deferred-media[autoplay]')) {
      this.#syncAutoplayForSlide(activeSlide);
      return;
    }

    const autoplaySlide = this.#getFirstAutoplaySlide();
    if (!(autoplaySlide instanceof HTMLElement)) return;

    const slideId = autoplaySlide.getAttribute('slide-id');
    const slideIndex = this.#getSlideIndex(autoplaySlide);
    if (slideId) {
      this.slideshow?.select({ id: slideId }, undefined, { animate: false });
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#syncAutoplayForSlide(autoplaySlide);
      });
      return;
    }
    if (slideIndex >= 0) {
      this.slideshow?.select(slideIndex, undefined, { animate: false });
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#syncAutoplayForSlide(autoplaySlide);
      });
      return;
    }

    this.#syncAutoplayForSlide(autoplaySlide);
  }

  #queueGridAutoplaySync() {
    requestAnimationFrame(() => {
      this.#syncGridAutoplay();
    });

    window.setTimeout(() => {
      this.#syncGridAutoplay();
    }, 180);
  }

  #syncGridAutoplay() {
    if (!this.isConnected) return;

    const grid = this.querySelector('.media-gallery__grid');
    if (!(grid instanceof HTMLElement)) return;

    const autoplayMedia = /** @type {DeferredMediaElement[]} */ ([...grid.querySelectorAll('deferred-media[autoplay]')]);
    if (!autoplayMedia.length) return;

    const selectedAutoplayMedia = autoplayMedia.find((media) => this.#isVisibleElement(media)) || autoplayMedia[0];
    if (!(selectedAutoplayMedia instanceof HTMLElement)) return;

    for (const media of autoplayMedia) {
      if (media === selectedAutoplayMedia) continue;
      media.pauseMedia?.();
    }

    const mediaContainer = selectedAutoplayMedia.closest('.product-media-container');
    if (!(mediaContainer instanceof HTMLElement)) return;

    const hasInlineLoadedMedia = selectedAutoplayMedia.querySelector('video, iframe[data-video-type]') != null;
    if (
      typeof selectedAutoplayMedia.loadContent === 'function' &&
      !selectedAutoplayMedia.hasAttribute('data-media-loaded') &&
      !hasInlineLoadedMedia
    ) {
      selectedAutoplayMedia.loadContent(false);
    }

    this.#ensureSelectedAutoplayPlayback(selectedAutoplayMedia, mediaContainer);
  }

  #getSlideIndex(slide) {
    const slides = this.slideshow?.refs?.slides;
    if (!Array.isArray(slides)) return -1;
    return slides.indexOf(slide);
  }

  #getActiveSlide() {
    const slideshowSlides = this.slideshow?.refs?.slides;
    if (Array.isArray(slideshowSlides) && slideshowSlides.length > 0) {
      const activeSlideshowSlide = slideshowSlides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
      return activeSlideshowSlide ?? slideshowSlides[0] ?? null;
    }

    const activeDomSlide = this.querySelector('slideshow-slide[aria-hidden="false"]');
    if (activeDomSlide instanceof HTMLElement) return activeDomSlide;

    const firstDomSlide = this.querySelector('slideshow-slide');
    return firstDomSlide instanceof HTMLElement ? firstDomSlide : null;
  }

  #getFirstAutoplaySlide() {
    const slideshowSlides = this.slideshow?.refs?.slides;
    if (Array.isArray(slideshowSlides) && slideshowSlides.length > 0) {
      const autoplaySlide = slideshowSlides.find((slide) => slide.querySelector('deferred-media[autoplay]') != null);
      if (autoplaySlide) return autoplaySlide;
    }

    const autoplayDomSlide = this.querySelector('slideshow-slide:has(deferred-media[autoplay])');
    return autoplayDomSlide instanceof HTMLElement ? autoplayDomSlide : null;
  }

  #isDesktopViewport() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  #isVisibleElement(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    const visibleArea = visibleWidth * visibleHeight;
    const totalArea = rect.width * rect.height;
    if (!totalArea) return false;

    return visibleArea / totalArea >= 0.15;
  }

  /**
   * First-time autoplay on mobile can race with slide visibility updates.
   * Retry play across a short window so the selected video starts reliably.
   * @param {DeferredMediaElement} media
   * @param {HTMLElement} slide
   */
  #ensureSelectedAutoplayPlayback(media, slide) {
    if (typeof media.playMedia !== 'function') return;

    media.playMedia();

    requestAnimationFrame(() => {
      if (!this.isConnected || !slide.contains(media)) return;
      media.playMedia?.();
    });

    window.setTimeout(() => {
      if (!this.isConnected || !slide.contains(media)) return;
      media.playMedia?.();
    }, 180);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const media = this.refs.zoomDialogComponent?.refs?.media;
    const zoomDialogMedia = media?.[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
